export const clinic = {
  name: "Alder Veterinary Clinic",
  short: "Alder Vet",
  address: "1428 Linden Avenue, Suite 200, Portland, OR 97214",
  phone: "(503) 555-0142",
  hours: [
    { day: "Mon – Fri", time: "8:00 – 18:30" },
    { day: "Saturday", time: "9:00 – 15:00" },
    { day: "Sunday", time: "Closed" },
  ],
  parkingNote: "Street parking on Linden Ave. Bus lines 14 & 20 stop one block east.",
};

export const services = [
  {
    slug: "wellness-care",
    index: "01",
    group: "Wellness & Preventive Care",
    title: "Wellness & Preventive Care",
    short: "Annual exams, vaccines, nutrition and lifelong health planning for cats and dogs.",
    covers: [
      "Comprehensive annual exam and body condition scoring",
      "Core and lifestyle-based vaccinations",
      "Parasite prevention plans",
      "Nutrition and weight guidance",
      "Puppy and kitten starter programs",
    ],
    whenToBook: "Once a year for adults, twice a year for seniors, or any time habits change.",
    expect: "A quiet, unhurried exam. Your clinician will walk through findings with you and leave a written plan.",
    related: ["dental-care", "diagnostics", "surgery"],
  },
  {
    slug: "dental-care",
    index: "02",
    group: "Dental Care",
    title: "Dental Care",
    short: "Cleanings, oral exams, digital dental radiography and extractions when needed.",
    covers: [
      "Full-mouth oral exam and charting",
      "Ultrasonic scaling and polishing under anesthesia",
      "Digital dental radiographs",
      "Extractions and oral surgery",
      "Home care coaching",
    ],
    whenToBook: "If you notice bad breath, tartar, red gums, or reduced appetite — or annually as prevention.",
    expect: "A pre-anesthetic exam, a same-day cleaning under monitored anesthesia, and a recovery check-in the next day.",
    related: ["wellness-care", "surgery", "diagnostics"],
  },
  {
    slug: "diagnostics",
    index: "03",
    group: "Diagnostics & Treatment",
    title: "Diagnostics & Treatment",
    short: "In-house labs, digital imaging and treatment for illness and chronic conditions.",
    covers: [
      "In-house bloodwork and urinalysis",
      "Digital radiography and ultrasound",
      "Chronic disease management",
      "Allergy and dermatology workups",
      "Pain management plans",
    ],
    whenToBook: "When something feels off — changes in appetite, energy, weight, thirst, or bathroom habits.",
    expect: "Most results the same day, a clear diagnosis when possible, and a treatment plan written in plain language.",
    related: ["wellness-care", "surgery", "urgent-care"],
  },
  {
    slug: "surgery",
    index: "04",
    group: "Surgery & Recovery",
    title: "Surgery & Recovery",
    short: "Soft tissue surgery, spay and neuter, mass removals, and attentive recovery care.",
    covers: [
      "Spay and neuter",
      "Soft tissue surgery",
      "Mass and lump removals",
      "Wound repair",
      "Post-op pain and recovery plans",
    ],
    whenToBook: "After a consult with your veterinarian. We'll schedule at a time that fits your household routine.",
    expect: "A calm pre-op morning, monitored anesthesia, warm recovery, and a phone call before you drive home.",
    related: ["dental-care", "diagnostics", "wellness-care"],
  },
  {
    slug: "urgent-care",
    index: "05",
    group: "Illness & Urgent Care",
    title: "Illness & Urgent Care",
    short: "Same-day sick visits for pets who are not feeling like themselves.",
    covers: [
      "Same-day triage",
      "GI upset, coughing, limping",
      "Ear and skin infections",
      "Minor wound care",
      "Referral coordination for emergencies",
    ],
    whenToBook: "Call us the same morning. We hold daily slots for pets who need to be seen quickly.",
    expect: "A calm exam room, quick diagnostics, and honest guidance about next steps.",
    related: ["diagnostics", "wellness-care", "dental-care"],
  },
];

export const team = [
  {
    name: "Dr. Maren Ellis",
    credential: "DVM, Fear-Free Certified",
    specialty: "Preventive care & senior wellness",
    img: "https://readdy.ai/api/search-image?query=A%20calm%20female%20veterinarian%20in%20her%20late%2030s%20wearing%20a%20soft%20olive%20scrub%20top%2C%20standing%20in%20a%20bright%20modern%20veterinary%20clinic%20with%20warm%20natural%20daylight%2C%20natural%20editorial%20portrait%2C%20soft%20contrast%2C%20real%20clinic%20environment%2C%20warm%20off%20white%20background%20with%20soft%20green%20accents%2C%20authentic%20human%20expression%20and%20gentle%20smile&width=480&height=600&seq=team-maren-1&orientation=portrait",
  },
  {
    name: "Dr. Theo Nakamura",
    credential: "DVM, MS Surgery",
    specialty: "Soft tissue surgery & dentistry",
    img: "https://readdy.ai/api/search-image?query=A%20warm%20male%20veterinarian%20in%20his%20early%2040s%20with%20short%20dark%20hair%20wearing%20a%20muted%20green%20scrub%20top%2C%20standing%20in%20a%20bright%20clean%20modern%20veterinary%20clinic%20with%20soft%20daylight%2C%20natural%20editorial%20portrait%2C%20warm%20off%20white%20background%2C%20real%20clinic%20environment%2C%20soft%20contrast%2C%20authentic%20calm%20confident%20expression&width=480&height=600&seq=team-theo-1&orientation=portrait",
  },
  {
    name: "Dr. Priya Rao",
    credential: "DVM, IVAS Acupuncture",
    specialty: "Chronic conditions & pain management",
    img: "https://readdy.ai/api/search-image?query=A%20thoughtful%20female%20veterinarian%20in%20her%20mid%2030s%20with%20dark%20hair%20tied%20back%20wearing%20a%20cream%20scrub%20top%2C%20standing%20in%20a%20soft%20bright%20veterinary%20clinic%20interior%20with%20natural%20light%2C%20natural%20editorial%20portrait%20photography%2C%20warm%20neutral%20off%20white%20background%2C%20authentic%20gentle%20expression%2C%20real%20clinic%20setting&width=480&height=600&seq=team-priya-1&orientation=portrait",
  },
];

export const faqs = [
  {
    q: "Do you accept new patients?",
    a: "Yes. We welcome new dogs, cats, and small companion animals. You can request an appointment online and our team will confirm within one business day.",
  },
  {
    q: "What pets do you treat?",
    a: "We care for dogs and cats of all life stages, and select small companion animals. If you're unsure, give us a call and we'll help you find the right care.",
  },
  {
    q: "What should I bring to my first visit?",
    a: "Any previous medical records, a list of current medications, and a favorite treat or toy. That's it — we'll handle the rest together.",
  },
  {
    q: "Do you offer same-day appointments?",
    a: "We hold daily same-day slots for sick visits. Call us in the morning and we'll do our best to see your pet that day.",
  },
  {
    q: "What should I do in an emergency?",
    a: "During clinic hours, call us right away. After hours, we recommend Rose City Veterinary Emergency at (503) 555-0199, and we'll follow up with you the next morning.",
  },
];